/**
 * CrewProfilePage ✦ — /crew/profile
 *
 * Identity + progression. Mobile-first. Single screen, every
 * field optional. The completion ring + XP bar are the gamified
 * centrepieces — empty profiles see 0%, cards prompt at each step.
 *
 * Per spec: feels gamified, encouraging, social-first. NOT CV-like.
 * Discord-prestige · Duolingo-streak · Fortnite-progression energy.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, SealCheck, Trophy, Lightning, UsersThree, FloppyDisk, Lock,
  CheckCircle, Circle, Sparkle, Share, ShieldCheck, Heart, Crown, ArrowClockwise,
} from '@phosphor-icons/react';
import ShareWithParentModal, { ShareWithParentTrigger } from '../../components/crew/ShareWithParentModal';
import BadgeUnlockOverlay from '../../components/crew/BadgeUnlockOverlay';
import FlexShareModal from '../../components/crew/FlexShareModal';
import CrewVideoUploader from '../../components/crew/CrewVideoUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BG = '#07071A';
const PANEL = 'rgba(255,255,255,0.05)';
const PANEL_BORDER = 'rgba(255,255,255,0.1)';
const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';
const FONT_STACK = `'Inter', 'Poppins', system-ui, sans-serif`;

const labelStyle = {
  display: 'block', fontFamily: FONT_STACK, fontSize: 11, fontWeight: 700,
  letterSpacing: '0.18em', textTransform: 'uppercase', color: TEXT_MUTED, marginBottom: 6,
};
const inputStyle = {
  width: '100%', padding: '13px 14px', background: 'rgba(255,255,255,0.04)',
  border: `1px solid ${PANEL_BORDER}`, borderRadius: 12,
  fontFamily: FONT_STACK, fontSize: 15, color: TEXT, outline: 'none',
};

const CompletionRing = ({ pct, size = 110 }) => {
  const radius = (size - 12) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (pct / 100) * circ;
  return (
    <div style={{ position: 'relative', width: size, height: size }} data-testid="profile-completion-ring">
      <svg width={size} height={size}>
        <defs>
          <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={PURPLE} />
            <stop offset="50%" stopColor={PINK} />
            <stop offset="100%" stopColor={BLUE} />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={radius}
          stroke="rgba(255,255,255,0.08)" strokeWidth="6" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={radius}
          stroke="url(#ringGrad)" strokeWidth="6" fill="none"
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1)' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        fontFamily: FONT_STACK, color: TEXT,
      }}>
        <span style={{ fontSize: 24, fontWeight: 900, letterSpacing: '-0.02em' }}>{pct}%</span>
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: TEXT_MUTED, marginTop: 2 }}>
          Complete
        </span>
      </div>
    </div>
  );
};

const StatTile = ({ icon: Icon, value, label, color }) => (
  <div style={{
    padding: 14, borderRadius: 14,
    background: PANEL, border: `1px solid ${PANEL_BORDER}`,
    minWidth: 0,
  }}>
    <Icon size={16} weight="fill" color={color} />
    <p style={{ margin: '8px 0 0', fontFamily: FONT_STACK, fontWeight: 900, fontSize: 22, color: TEXT, letterSpacing: '-0.02em' }}>
      {value}
    </p>
    <p style={{ margin: '2px 0 0', fontFamily: FONT_STACK, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: TEXT_MUTED }}>
      {label}
    </p>
  </div>
);

// ── CrewReputationCard ✦ — Reputation v2 ────────────────────
// Sits ABOVE the XP bar on /crew/profile. Surfaces:
//   • current Reputation Tier (Rising → Trusted → Favourite → Leader)
//   • progress hint to next tier (e.g. "2 more shifts · 1 reference to Crew Leader ✦")
//   • the 4 social trust signal badges (PRESENT / DORMANT)
//   • repeat-requested + favourite counts (the strongest social signals)
// Visual register: soft electric-blue glow, glassmorphic. NEVER a
// "score" — always a tier the crew member earns into.
const TIER_ICONS = {
  rising:     Sparkle,
  trusted:    ShieldCheck,
  favourite:  Heart,
  leader:     Crown,
};
const TIER_ACCENT = {
  rising:    BLUE,
  trusted:   PURPLE,
  favourite: PINK,
  leader:    '#FFD166', // GOLD
};

const CrewReputationCard = ({ reputation, onShareParent }) => {
  if (!reputation) return null;
  const tier = reputation.tier?.current || {};
  const tierId = tier.id || 'rising';
  const accent = TIER_ACCENT[tierId] || BLUE;
  const Icon = TIER_ICONS[tierId] || Sparkle;
  const signals = reputation.social_signals || [];
  const repeats = reputation.repeat_requests || 0;
  const favs = reputation.favourite_marks || 0;
  const uniqueSuppliers = reputation.unique_repeat_suppliers || 0;

  return (
    <section
      data-testid="reputation-card"
      data-tier={tierId}
      style={{
        padding: 20, borderRadius: 22, marginBottom: 14,
        background: `linear-gradient(135deg, ${accent}1A 0%, ${PURPLE}10 100%)`,
        border: `1px solid ${accent}55`,
        boxShadow: `0 12px 30px ${accent}22, inset 0 0 22px ${accent}10`,
        position: 'relative', overflow: 'hidden',
      }}
    >
      <div style={{
        position: 'absolute', top: -28, right: -28, width: 110, height: 110,
        borderRadius: '50%', background: accent, opacity: 0.12, filter: 'blur(28px)',
      }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 13,
          background: `linear-gradient(135deg, ${accent}, ${PURPLE})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 8px 22px ${accent}55`,
        }}>
          <Icon size={20} weight="fill" color="#fff" />
        </div>
        <div style={{ minWidth: 0 }}>
          <p style={{
            margin: 0, fontFamily: FONT_STACK, fontSize: 10.5, fontWeight: 800,
            letterSpacing: '0.32em', textTransform: 'uppercase', color: accent,
          }}>
            ✦ Crew Reputation
          </p>
          <p
            data-testid="reputation-tier-label"
            style={{
              margin: '4px 0 0', fontFamily: FONT_STACK,
              fontWeight: 900, fontSize: 22, letterSpacing: '-0.01em', color: TEXT,
            }}
          >
            {tier.label || 'Rising Crew'}
          </p>
        </div>
      </div>
      <p style={{ margin: '4px 0 12px', color: TEXT, fontSize: 13.5, lineHeight: 1.55 }}>
        {tier.blurb || "You've started something real."}
      </p>
      {reputation.tier?.progress_hint && (
        <p
          data-testid="reputation-progress-hint"
          style={{
            margin: '0 0 14px', color: accent, fontSize: 12.5, fontWeight: 700,
            fontStyle: 'italic',
          }}
        >
          {reputation.tier.progress_hint}
        </p>
      )}

      {/* Repeat-requested counters — the strongest social proof */}
      <div
        data-testid="reputation-counts"
        style={{
          display: 'grid', gap: 8, marginBottom: 14,
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
        }}
      >
        {[
          { id: 'repeat',     label: 'Requested again',  value: repeats,         accent: BLUE   },
          { id: 'favourite',  label: 'Favourite marks',  value: favs,            accent: PINK   },
          { id: 'breadth',    label: 'Supplier breadth', value: uniqueSuppliers, accent: PURPLE },
        ].map((c) => (
          <div
            key={c.id}
            data-testid={`reputation-count-${c.id}`}
            style={{
              padding: 12, borderRadius: 14, textAlign: 'center',
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${c.accent}33`,
            }}
          >
            <p style={{
              margin: 0, fontWeight: 900, fontSize: 22, color: c.accent,
              letterSpacing: '-0.02em',
            }}>{c.value}</p>
            <p style={{
              margin: '2px 0 0', fontSize: 10.5, color: TEXT_MUTED, fontWeight: 700,
              letterSpacing: '0.16em', textTransform: 'uppercase',
            }}>{c.label}</p>
          </div>
        ))}
      </div>

      {/* The 4 social trust signal badges — PRESENT / DORMANT */}
      <div
        data-testid="reputation-signals"
        style={{
          display: 'grid', gap: 8,
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        }}
      >
        {signals.map((s) => (
          <div
            key={s.id}
            data-testid={`reputation-signal-${s.id}`}
            data-unlocked={s.unlocked ? '1' : '0'}
            style={{
              padding: '10px 12px', borderRadius: 14,
              background: s.unlocked
                ? `linear-gradient(135deg, ${accent}26, ${PURPLE}14)`
                : 'rgba(255,255,255,0.03)',
              border: `1px solid ${s.unlocked ? accent + '88' : PANEL_BORDER}`,
              opacity: s.unlocked ? 1 : 0.6,
              boxShadow: s.unlocked ? `0 6px 16px ${accent}22` : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              {s.unlocked
                ? <Sparkle size={12} weight="fill" color={accent}
                    style={{ filter: `drop-shadow(0 0 6px ${accent}77)` }} />
                : <Lock size={12} weight="fill" color={TEXT_MUTED} />}
              <p style={{
                margin: 0, fontWeight: 900, fontSize: 12.5,
                color: s.unlocked ? TEXT : TEXT_MUTED,
                letterSpacing: '-0.005em',
              }}>
                {s.label}
              </p>
            </div>
            <p style={{
              margin: 0, fontSize: 11, color: TEXT_MUTED, lineHeight: 1.45,
            }}>
              {s.blurb}
            </p>
          </div>
        ))}
      </div>

      {onShareParent && (
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-start' }}>
          <ShareWithParentTrigger onOpen={onShareParent} testid="reputation-share-parent" />
        </div>
      )}
    </section>
  );
};

// XP bar with neon fill + glowing edge. Animates fill on prop change.
const XPBar = ({ progression }) => {
  if (!progression) return null;
  const { level, xp, xp_into_level, xp_for_next_level, next_badge } = progression;
  const pct = xp_for_next_level > 0
    ? Math.max(2, Math.min(100, Math.round((xp_into_level / xp_for_next_level) * 100)))
    : 100;
  return (
    <div
      data-testid="xp-bar"
      style={{
        padding: 18, borderRadius: 18, marginBottom: 22,
        background: `linear-gradient(135deg, ${BLUE}1A 0%, ${PURPLE}1A 100%)`,
        border: `1px solid ${PANEL_BORDER}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <p style={{
            margin: 0, fontFamily: FONT_STACK, fontSize: 10, fontWeight: 800,
            letterSpacing: '0.24em', textTransform: 'uppercase', color: BLUE,
          }}>
            Crew Level
          </p>
          <p data-testid="xp-level" style={{
            margin: '4px 0 0', fontWeight: 900, fontSize: 30, letterSpacing: '-0.02em',
          }}>
            {level}
          </p>
        </div>
        <p data-testid="xp-value" style={{
          margin: 0, fontFamily: FONT_STACK, fontSize: 12, color: TEXT_MUTED,
        }}>
          {xp} XP
        </p>
      </div>
      <div style={{
        height: 10, borderRadius: 999, background: 'rgba(255,255,255,0.06)',
        overflow: 'hidden', position: 'relative',
      }}>
        <div
          data-testid="xp-bar-fill"
          style={{
            width: `${pct}%`, height: '100%',
            background: `linear-gradient(90deg, ${PURPLE}, ${PINK}, ${BLUE})`,
            boxShadow: `0 0 14px ${PINK}AA`,
            transition: 'width 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </div>
      <p style={{
        margin: '10px 0 0', fontFamily: FONT_STACK, fontSize: 12.5, color: TEXT_MUTED,
      }}>
        {xp_for_next_level - xp_into_level} XP to Level {level + 1}
        {next_badge && (
          <>
            {' · '}
            <span data-testid="xp-next-badge" style={{ color: PINK, fontWeight: 700 }}>
              {next_badge.hint} → {next_badge.label}
            </span>
          </>
        )}
      </p>
    </div>
  );
};

const BadgeGallery = ({ badges, onFlex }) => {
  if (!badges || badges.length === 0) return null;
  return (
    <div style={{ marginBottom: 26 }}>
      <p style={{
        margin: '0 0 12px', fontSize: 11, fontWeight: 700, letterSpacing: '0.32em',
        textTransform: 'uppercase',
        background: `linear-gradient(135deg, ${PINK}, ${BLUE})`,
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>
        ✦ Badges · tap an unlocked one to flex it
      </p>
      <div
        data-testid="badge-gallery"
        style={{
          display: 'grid', gap: 10,
          gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
        }}
      >
        {badges.map((b) => (
          <button
            key={b.id}
            type="button"
            disabled={!b.unlocked}
            onClick={() => b.unlocked && onFlex?.(b)}
            data-testid={`badge-${b.id}`}
            data-unlocked={b.unlocked ? '1' : '0'}
            style={{
              padding: 14, borderRadius: 14, textAlign: 'center',
              background: b.unlocked
                ? `linear-gradient(135deg, ${PURPLE}33, ${PINK}33)`
                : 'rgba(255,255,255,0.03)',
              border: `1px solid ${b.unlocked ? PINK + '55' : PANEL_BORDER}`,
              boxShadow: b.unlocked ? `0 4px 16px ${PURPLE}33` : 'none',
              opacity: b.unlocked ? 1 : 0.55,
              cursor: b.unlocked ? 'pointer' : 'default',
              color: 'inherit', fontFamily: 'inherit',
            }}
            title={b.blurb}
          >
            <div style={{
              width: 42, height: 42, margin: '0 auto 8px',
              borderRadius: '50%',
              background: b.unlocked
                ? `radial-gradient(circle, ${PURPLE}, ${PINK})`
                : 'rgba(255,255,255,0.06)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: b.unlocked ? `0 0 18px ${PINK}55` : 'none',
            }}>
              {b.unlocked
                ? <Trophy size={18} weight="fill" color="#fff" />
                : <Lock size={16} weight="bold" color={TEXT_MUTED} />}
            </div>
            <p style={{
              margin: 0, fontFamily: FONT_STACK, fontWeight: 800, fontSize: 12,
              color: b.unlocked ? TEXT : TEXT_MUTED, letterSpacing: '-0.01em',
            }}>
              {b.label}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
};

const ConfidenceJourney = ({ steps }) => {
  if (!steps || steps.length === 0) return null;
  return (
    <div style={{ marginBottom: 28 }}>
      <p style={{
        margin: '0 0 4px', fontSize: 11, fontWeight: 700, letterSpacing: '0.32em',
        textTransform: 'uppercase',
        background: `linear-gradient(135deg, ${BLUE}, ${PURPLE})`,
        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>
        ✦ Confidence Journey
      </p>
      <p style={{ color: TEXT_MUTED, fontSize: 13, margin: '0 0 14px', lineHeight: 1.5 }}>
        Quiet milestones on the way to becoming the crew member every supplier remembers.
      </p>
      <div
        data-testid="confidence-journey"
        style={{ display: 'grid', gap: 8 }}
      >
        {steps.map((s) => (
          <div
            key={s.id}
            data-testid={`journey-${s.id}`}
            data-reached={s.reached ? '1' : '0'}
            style={{
              padding: '12px 14px', borderRadius: 12,
              background: s.reached ? `${PURPLE}1F` : 'rgba(255,255,255,0.03)',
              border: `1px solid ${s.reached ? PURPLE + '55' : PANEL_BORDER}`,
              display: 'flex', alignItems: 'center', gap: 10,
              opacity: s.reached ? 1 : 0.65,
            }}
          >
            {s.reached
              ? <CheckCircle size={18} weight="fill" color={BLUE} />
              : <Circle size={18} weight="bold" color={TEXT_MUTED} />}
            <span style={{
              fontFamily: FONT_STACK, fontSize: 14, fontWeight: 700,
              color: s.reached ? TEXT : TEXT_MUTED,
            }}>
              {s.label}
            </span>
            {s.reached && <Sparkle size={12} weight="fill" color={PINK} style={{ marginLeft: 'auto' }} />}
          </div>
        ))}
      </div>
    </div>
  );
};

const CrewProfilePage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [progression, setProgression] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [error, setError] = useState('');
  const [unlockBadges, setUnlockBadges] = useState([]);
  const [flexShare, setFlexShare] = useState(null);
  const [reputation, setReputation] = useState(null);
  const [showShareParent, setShowShareParent] = useState(false);
  const [parentBanner, setParentBanner] = useState(null);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [p, prog, rep, snap] = await Promise.all([
        axios.get(`${API}/crew/me/profile`),
        axios.get(`${API}/crew/me/progression`),
        axios.get(`${API}/crew/me/reputation`),
        axios.get(`${API}/crew/me/parent-snapshot`).catch(() => ({ data: { active: false } })),
      ]);
      setProfile(p.data);
      setProgression(prog.data);
      setReputation(rep.data);
      // Detect "your parent opened your snapshot ✦" — a soft, one-time
      // in-app banner. Triggers when the snapshot has been opened by
      // the parent but the crew member hasn't yet dismissed the banner.
      const s = snap.data?.snapshot;
      if (snap.data?.active && s?.first_opened_at && !s.banner_acknowledged_at) {
        setParentBanner({ first_opened_at: s.first_opened_at });
      } else {
        setParentBanner(null);
      }
      setDraft({
        name: p.data.name || '',
        age: p.data.age || '',
        city: p.data.city || '',
        bio: p.data.bio || '',
        interests: (p.data.interests || []).join(', '),
        availability: p.data.availability || '',
        photo_url: p.data.photo_url || '',
        intro_video_url: p.data.intro_video_url || '',
        intro_video_poster_url: p.data.intro_video_poster_url || '',
        digest_opt_in: p.data.digest_opt_in !== false,
      });
      // Surface newly-unlocked badges in the burst overlay. The
      // backend resets the "newly" set after this call, so each
      // unlock fires exactly once.
      if (prog.data?.newly_unlocked?.length) {
        const cat = prog.data.badges.filter((b) => prog.data.newly_unlocked.includes(b.id));
        if (cat.length) setUnlockBadges(cat);
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login?next=/crew/profile');
        return;
      }
      setError(err?.response?.data?.detail || 'Could not load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const save = async () => {
    setBusy(true); setError('');
    try {
      const payload = {
        name: draft.name,
        age: draft.age ? parseInt(draft.age, 10) : null,
        city: draft.city,
        bio: draft.bio,
        interests: draft.interests
          ? draft.interests.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        availability: draft.availability,
        photo_url: draft.photo_url,
        intro_video_url: draft.intro_video_url,
        intro_video_poster_url: draft.intro_video_poster_url,
      };
      const r = await axios.post(`${API}/crew/me/profile`, payload);
      setProfile(r.data);
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 1600);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save profile');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: BG, color: TEXT, fontFamily: FONT_STACK,
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Loading profile…
      </div>
    );
  }

  return (
    <div data-testid="crew-profile-page" style={{
      minHeight: '100vh', background: BG, color: TEXT, fontFamily: FONT_STACK,
      padding: 'clamp(20px, 5vw, 56px) clamp(16px, 5vw, 32px) 80px',
    }}>
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: `radial-gradient(circle at 18% 14%, ${PURPLE}1F 0%, transparent 42%), radial-gradient(circle at 85% 80%, ${PINK}1A 0%, transparent 42%)`,
      }} />
      <div style={{ maxWidth: 640, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <button
          onClick={() => navigate('/crew/feed')}
          data-testid="profile-back"
          style={{
            background: 'transparent', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 24,
          }}
        >
          <ArrowLeft size={14} weight="bold" /> Feed
        </button>

        {/* ── Header — completion ring + crew level ── */}
        <div style={{
          padding: 24, borderRadius: 22,
          background: `linear-gradient(135deg, ${PURPLE}1F 0%, ${PINK}14 100%)`,
          border: `1px solid ${PANEL_BORDER}`,
          display: 'flex', alignItems: 'center', gap: 22,
          marginBottom: 24,
        }}>
          <CompletionRing pct={profile.completion_pct || 0} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0, fontFamily: FONT_STACK, fontSize: 10, fontWeight: 800,
              letterSpacing: '0.22em', textTransform: 'uppercase', color: PINK,
            }}>
              Crew Level {progression?.level || profile.crew_level || 1}
            </p>
            <p style={{ margin: '6px 0 6px', fontFamily: FONT_STACK, fontWeight: 900, fontSize: 22, letterSpacing: '-0.02em' }}>
              {profile.name || 'Your crew profile'}
            </p>
            <p style={{ margin: 0, fontFamily: FONT_STACK, fontSize: 13, color: TEXT_MUTED, lineHeight: 1.5 }}>
              {profile.completion_pct >= 100
                ? "Your profile's complete. Suppliers love a face."
                : profile.completion_pct >= 50
                  ? "You're halfway there — finish the details to stand out."
                  : "Add a photo and a quick bio to land your first shift."}
            </p>
          </div>
        </div>

        {/* ── "Your parent opened your snapshot ✦" — soft, dismissable ── */}
        {parentBanner && (
          <div
            data-testid="parent-opened-banner"
            style={{
              padding: '14px 16px', borderRadius: 18, marginBottom: 14,
              background: 'linear-gradient(135deg, rgba(94,169,255,0.10), rgba(140,123,255,0.10))',
              border: '1px solid rgba(94,169,255,0.35)',
              boxShadow: '0 8px 22px rgba(94,169,255,0.18)',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              animation: 'crewSoftFade 600ms ease-out both',
            }}
          >
            <Heart size={16} weight="fill" color="#FF7AC2" style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <p style={{
                margin: 0, fontFamily: FONT_STACK, fontWeight: 800, fontSize: 14.5,
                color: TEXT, letterSpacing: '-0.005em',
              }}>
                Your parent opened your snapshot ✦
              </p>
              <p style={{ margin: '4px 0 0', color: TEXT_MUTED, fontSize: 12.5, lineHeight: 1.55 }}>
                Thanks for sharing your EventDrop Crew journey.
              </p>
            </div>
            <button
              onClick={async () => {
                try {
                  await axios.post(`${API}/crew/me/parent-snapshot/acknowledge-open`, {});
                } catch {}
                setParentBanner(null);
              }}
              data-testid="parent-opened-banner-dismiss"
              style={{
                background: 'transparent', border: 'none', color: TEXT_MUTED,
                cursor: 'pointer', fontSize: 14, padding: 4,
              }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* ── Crew Reputation card (Reputation v2 ✦) ── */}
        <CrewReputationCard reputation={reputation} onShareParent={() => setShowShareParent(true)} />

        <ShareWithParentModal open={showShareParent} onClose={() => setShowShareParent(false)} />

        {/* ── XP bar + next badge hint ── */}
        <XPBar progression={progression} />

        {/* "Flex your level" press — surfaces alongside XP for the
            user who hasn't unlocked a badge yet but still wants to
            show off the level grind. */}
        {progression && (
          <button
            onClick={() => setFlexShare({
              kind: 'level',
              payload: {
                id: `level-${progression.level}`,
                label: `Level ${progression.level}`,
                blurb: `Level ${progression.level} Crew Member`,
                name: profile.name || 'Crew',
                level: progression.level,
              },
            })}
            data-testid="profile-flex-level"
            style={{
              marginTop: -8, marginBottom: 24,
              padding: '10px 18px', borderRadius: 999, border: 'none',
              cursor: 'pointer', color: TEXT, fontFamily: FONT_STACK,
              fontSize: 12.5, fontWeight: 800,
              background: 'rgba(255,255,255,0.06)',
              boxShadow: `0 0 0 1px ${PINK}55 inset`,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            <Share size={12} weight="fill" /> Flex your level ✦
          </button>
        )}

        {/* ── Stats row ── */}
        <div style={{
          display: 'grid', gap: 10, marginBottom: 26,
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
        }}>
          <StatTile icon={Lightning} value={progression?.events_completed ?? profile.events_completed ?? 0} label="Events" color={PINK} />
          <StatTile icon={Trophy} value={progression?.references_earned ?? profile.references_earned ?? 0} label="Refs" color={BLUE} />
          <StatTile icon={UsersThree} value={progression?.repeat_bookings ?? 0} label="Repeats" color={PURPLE} />
          <StatTile icon={SealCheck} value={`${profile.reliability_score || 100}%`} label="Reliable" color={PINK} />
        </div>

        {/* ── Badges ── */}
        <BadgeGallery
          badges={progression?.badges}
          onFlex={(b) => setFlexShare({
            kind: 'badge',
            payload: {
              id: b.id,
              label: b.label,
              blurb: b.blurb,
              name: profile.name || 'Crew',
              level: progression?.level || profile.crew_level || 1,
            },
          })}
        />

        {/* ── Confidence Journey ── */}
        <ConfidenceJourney steps={progression?.confidence_journey} />

        {/* ── Edit form ── */}
        <p style={{
          margin: '0 0 6px', fontSize: 11, fontWeight: 700, letterSpacing: '0.32em',
          textTransform: 'uppercase',
          background: `linear-gradient(135deg, ${PINK}, ${BLUE})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          ✦ Your details
        </p>
        <p style={{ color: TEXT_MUTED, fontSize: 13.5, marginBottom: 22, lineHeight: 1.55 }}>
          Add what feels right. Every field nudges the ring further. No CVs, ever.
        </p>

        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <label style={labelStyle} htmlFor="cp-photo">Photo URL</label>
            <input id="cp-photo" data-testid="cp-photo" style={inputStyle}
              placeholder="Paste a photo link"
              value={draft.photo_url || ''}
              onChange={(e) => setDraft({ ...draft, photo_url: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle}>10-sec intro video (optional)</label>
            <CrewVideoUploader
              kind="profile"
              maxSeconds={10}
              value={{ video_url: draft.intro_video_url, poster_url: draft.intro_video_poster_url }}
              onChange={(next) => setDraft((d) => ({
                ...d,
                intro_video_url: next.video_url || '',
                intro_video_poster_url: next.poster_url || '',
              }))}
              testId="cp-video"
            />
          </div>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            <div>
              <label style={labelStyle} htmlFor="cp-name">Name</label>
              <input id="cp-name" data-testid="cp-name" style={inputStyle}
                value={draft.name || ''}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle} htmlFor="cp-age">Age</label>
              <input id="cp-age" data-testid="cp-age" type="number" min="13" max="99" style={inputStyle}
                value={draft.age || ''}
                onChange={(e) => setDraft({ ...draft, age: e.target.value })} />
            </div>
            <div>
              <label style={labelStyle} htmlFor="cp-city">City</label>
              <input id="cp-city" data-testid="cp-city" style={inputStyle}
                value={draft.city || ''}
                onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
            </div>
          </div>
          <div>
            <label style={labelStyle} htmlFor="cp-bio">Bio</label>
            <textarea id="cp-bio" data-testid="cp-bio" rows={3} maxLength={280}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: FONT_STACK, lineHeight: 1.5 }}
              placeholder="A line or two about you and what you'd love to learn."
              value={draft.bio || ''}
              onChange={(e) => setDraft({ ...draft, bio: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle} htmlFor="cp-interests">Interests (comma-separated)</label>
            <input id="cp-interests" data-testid="cp-interests" style={inputStyle}
              placeholder="Photography, Festivals, Hospitality"
              value={draft.interests || ''}
              onChange={(e) => setDraft({ ...draft, interests: e.target.value })} />
          </div>
          <div>
            <label style={labelStyle} htmlFor="cp-availability">Availability</label>
            <input id="cp-availability" data-testid="cp-availability" style={inputStyle}
              placeholder="Weekends · Evenings · Summer holidays"
              value={draft.availability || ''}
              onChange={(e) => setDraft({ ...draft, availability: e.target.value })} />
          </div>

          {error && (
            <p data-testid="cp-error" style={{ color: PINK, fontSize: 13 }}>{error}</p>
          )}

          {/* Daily digest opt-in — separate from the profile fields
              because it's a preference rather than identity. Toggle
              persists with its own endpoint so saving the profile
              and changing the digest setting are independent. */}
          <div
            data-testid="cp-digest-card"
            style={{
              padding: 16, borderRadius: 16,
              background: `linear-gradient(135deg, ${PURPLE}14 0%, ${BLUE}10 100%)`,
              border: `1px solid ${BLUE}33`,
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}
          >
            <div style={{
              width: 38, height: 38, borderRadius: 12,
              background: `linear-gradient(135deg, ${PURPLE}, ${BLUE})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Sparkle size={16} weight="fill" color="#fff" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 800, fontSize: 14, color: TEXT }}>
                Daily digest ✦ "Crew Wins"
              </p>
              <p style={{ margin: '3px 0 0', color: TEXT_MUTED, fontSize: 12.5, lineHeight: 1.5 }}>
                One short, visual recap at 18:30 — top flexes, fresh badges, hot urgent shifts. Mute anytime.
              </p>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={async () => {
                    const next = !draft.digest_opt_in;
                    setDraft({ ...draft, digest_opt_in: next });
                    try {
                      await axios.post(`${API}/crew/me/digest-opt-in`, { digest_opt_in: next });
                    } catch { /* silent — UI already flipped */ }
                  }}
                  data-testid="cp-digest-toggle"
                  data-on={draft.digest_opt_in ? '1' : '0'}
                  style={{
                    padding: '8px 14px', borderRadius: 999,
                    border: `1px solid ${draft.digest_opt_in ? PINK : 'rgba(255,255,255,0.12)'}`,
                    background: draft.digest_opt_in
                      ? `linear-gradient(135deg, ${PURPLE}, ${PINK})`
                      : 'transparent',
                    color: draft.digest_opt_in ? TEXT : TEXT_MUTED,
                    cursor: 'pointer', fontFamily: FONT_STACK,
                    fontSize: 12, fontWeight: 800,
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                  }}
                >
                  {draft.digest_opt_in ? 'Subscribed ✦' : 'Turn on'}
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/crew/digest/send-test`);
                      setSavedToast(true);
                      setTimeout(() => setSavedToast(false), 1800);
                    } catch (err) {
                      setError(err?.response?.data?.detail || 'Could not send test');
                    }
                  }}
                  data-testid="cp-digest-test"
                  style={{
                    padding: '8px 14px', borderRadius: 999,
                    background: 'transparent', border: `1px solid ${BLUE}55`,
                    color: BLUE, cursor: 'pointer', fontFamily: FONT_STACK,
                    fontSize: 12, fontWeight: 800,
                  }}
                >
                  Email me a preview
                </button>
              </div>
            </div>
          </div>

          <button
            onClick={save}
            disabled={busy}
            data-testid="cp-save"
            style={{
              marginTop: 6, padding: '15px 24px', borderRadius: 999, border: 'none',
              cursor: busy ? 'wait' : 'pointer', color: TEXT, fontFamily: FONT_STACK,
              fontSize: 15, fontWeight: 900, opacity: busy ? 0.7 : 1,
              background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
              boxShadow: `0 10px 28px ${PURPLE}55`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <FloppyDisk size={14} weight="fill" />
            {busy ? 'Saving…' : (savedToast ? 'Saved ✦' : 'Save profile')}
          </button>
        </div>
      </div>

      {unlockBadges.length > 0 && (
        <BadgeUnlockOverlay
          badges={unlockBadges}
          onClose={() => setUnlockBadges([])}
          crewName={profile.name || 'Crew'}
          crewLevel={progression?.level || profile.crew_level || 1}
        />
      )}

      <FlexShareModal
        open={!!flexShare}
        onClose={() => setFlexShare(null)}
        kind={flexShare?.kind || 'badge'}
        payload={flexShare?.payload || {}}
      />
    </div>
  );
};

export default CrewProfilePage;
